/**
 * @module validators
 *
 * Validator factories for file upload row validation.
 *
 * These factories create validator functions that check individual rows for
 * data quality issues after parsing and transformation. Each validator follows
 * a consistent pattern: it returns `null` if the row passes, or an error object
 * `{ field, value, message }` if it fails.
 *
 * **Architecture role:** Validators run AFTER transforms in the upload pipeline.
 * This means date fields have already been coerced to `YYYY-MM-DD` strings and
 * numeric fields have been coerced to numbers before validation sees them.
 *
 * **Error format:** All validators return errors with the same shape:
 * ```js
 * { field: string, value: any, message: string }
 * ```
 * The `useFileUploadProcessor` hook augments these with `{ row: number, type: 'validation' }`
 * before adding them to the errors array.
 *
 * **Usage patterns:**
 * 1. **Automatic via `buildValidatorsFromFieldDefs`** -- extracts `required`,
 *    `min`/`max`, and `pattern` rules from your fieldDefs and builds a combined
 *    validator. Used internally by `useUploadConfig`.
 * 2. **Manual composition** -- create individual validators with the factory
 *    functions and combine them with `combineValidators`.
 * 3. **Direct use** -- pass a single validator function as `validateRow` to
 *    `useFileUploadProcessor`.
 *
 * @example <caption>Automatic from fieldDefs (recommended)</caption>
 * import { buildValidatorsFromFieldDefs } from '@1823-partners/core';
 *
 * const fieldDefs = {
 *   CUSIP: { required: true, pattern: /^[A-Z0-9]{9}$/, patternMessage: 'Invalid CUSIP format' },
 *   BOOK_YIELD: { min: 0, max: 100 },
 *   LOAN_NAME: { required: true },
 * };
 *
 * const validateRow = buildValidatorsFromFieldDefs(fieldDefs);
 * // validateRow checks: CUSIP required + pattern, LOAN_NAME required, BOOK_YIELD range
 *
 * @example <caption>Manual composition</caption>
 * import { createRequiredValidator, createRangeValidator, combineValidators } from '@1823-partners/core';
 *
 * const validateRow = combineValidators(
 *   createRequiredValidator(['CUSIP', 'BOOK']),
 *   createRangeValidator('BOOK_YIELD', 0, 100),
 *   createRangeValidator('PRICE', 0, 200, 'Price must be between 0 and 200'),
 * );
 */
import { combineValidators } from '../grid/useFileUploadProcessor';

/**
 * @typedef {Object} ValidationError
 * @property {string} field - The field name that failed validation
 * @property {*} value - The value that failed (null for required-field failures)
 * @property {string} message - Human-readable error message
 */

/**
 * Creates a validator that checks whether a field value is a number within [min, max].
 *
 * Skips validation when the value is `null`, `undefined`, or empty string --
 * use `createRequiredValidator` separately if the field must be present.
 *
 * The value is coerced to a number via `Number(value)` before checking the range.
 * If the coercion produces `NaN`, the range error is returned.
 *
 * @param {string} field - Field name to validate (e.g., `'BOOK_YIELD'`)
 * @param {number} min - Minimum allowed value (inclusive)
 * @param {number} max - Maximum allowed value (inclusive)
 * @param {string} [message] - Custom error message. Defaults to
 *   `"${field} must be between ${min} and ${max}"`.
 * @returns {Function} Validator function with signature `(row) => ValidationError | null`.
 *   Returns `null` if the value passes or is not present.
 *
 * @example
 * const validateYield = createRangeValidator('BOOK_YIELD', 0, 100);
 * validateYield({ BOOK_YIELD: 3.5 });    // null (passes)
 * validateYield({ BOOK_YIELD: 150 });     // { field: 'BOOK_YIELD', value: 150, message: '...' }
 * validateYield({ BOOK_YIELD: '' });      // null (skipped -- use required validator separately)
 * validateYield({ BOOK_YIELD: 'abc' });   // { field: 'BOOK_YIELD', value: 'abc', message: '...' }
 *
 * @example <caption>With custom message</caption>
 * const validatePrice = createRangeValidator('PRICE', 0, 200, 'Price out of range (0-200)');
 */
export const createRangeValidator = (field, min, max, message) => {
  return (row) => {
    const value = row[field];

    // Skip if not provided
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const num = Number(value);
    if (isNaN(num)) {
      return {
        field,
        value,
        message: message || `${field} must be between ${min} and ${max}`,
      };
    }

    if (num < min || num > max) {
      return {
        field,
        value,
        message: message || `${field} must be between ${min} and ${max}`,
      };
    }

    return null;
  };
};

/**
 * Creates a validator that checks that each of the specified fields has a
 * non-null, non-undefined, non-empty-string value.
 *
 * Returns an error for the **first** missing field encountered (short-circuits).
 * This means if both CUSIP and BOOK are missing, only the CUSIP error is returned.
 * The `combineValidators` wrapper will stop at this error for the row.
 *
 * @param {string[]} fields - Array of field names that are required.
 *   Order matters -- the first missing field in this array triggers the error.
 * @returns {Function} Validator function with signature `(row) => ValidationError | null`.
 *
 * @example
 * const validateRequired = createRequiredValidator(['CUSIP', 'BOOK']);
 * validateRequired({ CUSIP: '14913UAN0', BOOK: 'AFS' });  // null (passes)
 * validateRequired({ CUSIP: '', BOOK: 'AFS' });            // { field: 'CUSIP', value: null, message: 'CUSIP is required' }
 * validateRequired({ CUSIP: '14913UAN0' });                 // { field: 'BOOK', value: null, message: 'BOOK is required' }
 */
export const createRequiredValidator = (fields) => {
  return (row) => {
    for (const field of fields) {
      const value = row[field];
      if (value === null || value === undefined || value === '') {
        return {
          field,
          value: null,
          message: `${field} is required`,
        };
      }
    }
    return null;
  };
};

/**
 * Creates a validator that checks whether a field value matches a regular expression.
 *
 * Skips validation when the value is `null`, `undefined`, or empty string --
 * use `createRequiredValidator` separately if the field must be present.
 *
 * The value is coerced to a string via `String(value)` before testing the pattern.
 *
 * @param {string} field - Field name to validate (e.g., `'CUSIP'`)
 * @param {RegExp} pattern - Regular expression to test against. Must match the
 *   entire expected format (use `^...$` anchors).
 * @param {string} message - Error message when the pattern does not match.
 *   Unlike range validators, there is no default message -- you must provide one
 *   because patterns are too varied for a generic message to be useful.
 * @returns {Function} Validator function with signature `(row) => ValidationError | null`.
 *
 * @example
 * const validateCusip = createPatternValidator(
 *   'CUSIP',
 *   /^[A-Z0-9]{9}$/,
 *   'CUSIP must be 9 alphanumeric characters'
 * );
 * validateCusip({ CUSIP: '14913UAN0' });  // null (passes)
 * validateCusip({ CUSIP: 'short' });      // { field: 'CUSIP', value: 'short', message: '...' }
 * validateCusip({ CUSIP: '' });           // null (skipped)
 */
export const createPatternValidator = (field, pattern, message) => {
  return (row) => {
    const value = row[field];

    // Skip if not provided
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (!pattern.test(String(value))) {
      return {
        field,
        value,
        message,
      };
    }

    return null;
  };
};

/**
 * Creates a validator that checks whether a field value is one of the allowed choices.
 *
 * Skips validation when the value is `null`, `undefined`, or empty string --
 * use `createRequiredValidator` separately if the field must be present.
 *
 * The value is coerced to a trimmed string via `String(value).trim()` before
 * checking membership. The comparison is **case-sensitive** -- if your choices
 * list contains `"Buy"`, the value `"buy"` will fail.
 *
 * @param {string} field - Field name to validate (e.g., `'DIRECTION'`)
 * @param {Array<string|number>} choices - Array of allowed values.
 * @param {string} [message] - Custom error message. Defaults to
 *   `"${field} must be one of: ${choices.join(', ')}"`. When the choices list
 *   is long (>10 items), the default message is truncated to the first 10.
 * @returns {Function} Validator function with signature `(row) => ValidationError | null`.
 *   Returns `null` if the value passes or is not present.
 *
 * @example
 * const validateDirection = createChoicesValidator('DIRECTION', ['Buy', 'Sell']);
 * validateDirection({ DIRECTION: 'Buy' });     // null (passes)
 * validateDirection({ DIRECTION: 'Hold' });    // { field: 'DIRECTION', value: 'Hold', message: '...' }
 * validateDirection({ DIRECTION: '' });        // null (skipped -- use required validator separately)
 *
 * @example <caption>With custom message</caption>
 * const validateCurrency = createChoicesValidator('CURRENCY', ['USD', 'EUR'], 'Invalid currency');
 */
export const createChoicesValidator = (field, choices, message) => {
  const choiceStrings = choices.map(c => String(c).trim());
  return (row) => {
    const value = row[field];

    // Skip if not provided
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const valueStr = String(value).trim();
    if (!choiceStrings.includes(valueStr)) {
      const defaultMsg = choiceStrings.length > 10
        ? `${field} must be one of: ${choiceStrings.slice(0, 10).join(', ')}, ... (${choiceStrings.length} options)`
        : `${field} must be one of: ${choiceStrings.join(', ')}`;
      return {
        field,
        value,
        message: message || defaultMsg,
      };
    }

    return null;
  };
};

/**
 * Builds a combined validator from field definitions that include validation rules.
 *
 * This is the highest-level validator builder. It scans each field definition for
 * `required`, `min`/`max`, and `pattern`/`patternMessage` properties, creates the
 * appropriate individual validators, and combines them with `combineValidators`.
 *
 * **Execution order:** Required-field checks run first (so missing fields surface
 * before range, choices, or pattern errors). Then choices validators, then range
 * validators, then pattern validators, in the order fields appear in the
 * definitions object.
 *
 * **Integration with useUploadConfig:** When you pass `fieldDefs` to `useUploadConfig`,
 * it calls this function internally to build the validator. You only need to call
 * this directly if you are using `useFileUploadProcessor` without `useUploadConfig`.
 *
 * @param {Object<string, Object>} fieldDefs - Map of field name to definition object.
 *   Each value may include any combination of:
 *   - `required` {boolean} - Whether the field must be non-empty
 *   - `choices` {Array} - Array of allowed values (triggers choices validation)
 *   - `choicesMessage` {string} - Custom error message for choices failures
 *   - `min` {number} - Minimum value (both `min` and `max` required for range check)
 *   - `max` {number} - Maximum value (both `min` and `max` required for range check)
 *   - `pattern` {RegExp} - Regex pattern to test the field value against
 *   - `patternMessage` {string} - Error message for pattern failures
 *
 *   Fields without any of these properties are silently skipped.
 * @returns {Function} Combined validator function with signature
 *   `(row, index) => ValidationError | null`. Returns `null` if all checks pass,
 *   or the first error encountered.
 *
 * @example <caption>Complete field definitions with validation</caption>
 * import { buildValidatorsFromFieldDefs } from '@1823-partners/core';
 *
 * const fieldDefs = {
 *   CUSIP: {
 *     required: true,
 *     pattern: /^[A-Z0-9]{9}$/,
 *     patternMessage: 'CUSIP must be 9 alphanumeric characters',
 *   },
 *   DIRECTION: {
 *     choices: ['Buy', 'Sell'],
 *   },
 *   BOOK_YIELD: {
 *     min: 0,
 *     max: 100,
 *   },
 *   LOAN_NAME: {
 *     required: true,
 *   },
 *   NOTES: {
 *     // No validation rules -- this field is skipped
 *   },
 * };
 *
 * const validateRow = buildValidatorsFromFieldDefs(fieldDefs);
 *
 * // Pass:
 * validateRow({ CUSIP: '14913UAN0', BOOK_YIELD: 3.5, LOAN_NAME: 'Main St' });  // null
 *
 * // Fail (required):
 * validateRow({ CUSIP: '', BOOK_YIELD: 3.5, LOAN_NAME: 'Main St' });
 * // { field: 'CUSIP', value: null, message: 'CUSIP is required' }
 *
 * // Fail (range):
 * validateRow({ CUSIP: '14913UAN0', BOOK_YIELD: 150, LOAN_NAME: 'Main St' });
 * // { field: 'BOOK_YIELD', value: 150, message: 'BOOK_YIELD must be between 0 and 100' }
 */
export const buildValidatorsFromFieldDefs = (fieldDefs) => {
  const validators = [];
  const requiredFields = [];

  for (const [field, def] of Object.entries(fieldDefs)) {
    if (!def) continue;

    if (def.required) {
      requiredFields.push(field);
    }

    if (def.choices && Array.isArray(def.choices)) {
      validators.push(createChoicesValidator(field, def.choices, def.choicesMessage));
    }

    if (def.min !== undefined && def.max !== undefined) {
      validators.push(createRangeValidator(field, def.min, def.max));
    }

    if (def.pattern) {
      validators.push(createPatternValidator(field, def.pattern, def.patternMessage));
    }
  }

  if (requiredFields.length > 0) {
    // Required check runs first so missing fields surface before range/pattern errors
    validators.unshift(createRequiredValidator(requiredFields));
  }

  return combineValidators(...validators);
};
